import { Component, Inject, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FlexibleSelectionFormFieldComponent } from '../../../../../shared/components/form-fields/flexible-selection-form-field/flexible-selection-form-field.component';
import { FormFieldService } from '../../../../../shared/services/form-field.service';
import { FormFieldCascadingDropdownConstraint } from '../../../../../shared/models/form-builder/field-constraints/field-cascading-dropdown-constraint.model';
import { CurrentSiteService } from '../../../../../shared/services/current-site.service';
import { ListConstraintColumn } from '../../../../models/flexible-selection-fields/list-constraint-column.model';
import { FlexibleSelectionFieldService } from '../../../../services/flexible-selection-field.service';
import { FormFieldListValuesConstraint } from '../../../../models/form-builder/field-constraints/form-field-list-values-constraint.model';
import { FormField } from '../../../../models/form-builder/form-field.model';
import { FormFieldConstraintTypeEnum } from '../../../../enums/form-field-constraint-type.enum';
import { ListConstraintValue } from '../../../../models/flexible-selection-fields/list-constraint-value.model';
import { AllFieldInfo } from '../../../../models/field-conditional-logic/conditional-logic-editor-classes';
import { FieldDefinitionService } from '../../../../services/field-definition.service';
import { IFieldDefinitionLogic } from '../../../../interfaces/ifield-definition-logic.interface';
import { FieldDefinition } from '../../../../models/form-builder/field-definition.model';
import { Form } from '../../../../models/form-builder/form.model';
import { FormFieldPropertyEnum } from '../../../../models/form-builder/form-field-property-enum.model';
import { StringUtil } from '../../../../utility-classes/string.util';

export class FormFieldListConstraintColumn {
    public formFieldId: number;
    public formFieldName: string;
    public constraintId: number;
    public constraintName: string;
    public listConstraintColumnId: number;
    public listConstraintColumnName: string;

    public constructor(formFieldId: number, columnId: number) {
        this.formFieldId = formFieldId;
        this.listConstraintColumnId = columnId;
    }
}

/*
    Note: this component is used as a dialog and is opened from
    - form-builder.component.ts
    - gird-field-designer.component.ts
*/
@Component({
    selector: 'app-flexible-selection-field-configuration',
    templateUrl: './flexible-selection-field-configuration.component.html',
    styleUrls: ['./flexible-selection-field-configuration.component.scss'],
    standalone: false
})
export class FlexibleSelectionFieldConfigurationComponent implements OnInit {

    @Output() save: EventEmitter<FlexibleSelectionFormFieldComponent> = new EventEmitter();

    ngFormGroup: UntypedFormGroup;

    Constraints: FormFieldListValuesConstraint[];
    loadingConstraints: boolean = false;
    Columns: ListConstraintColumn[];
    loadingColumns: boolean = false;
    AvailableDefaultValues: ListConstraintValue[];
    SelectedConstraintId: number;
    SelectedColumnId: number;
    formField: FormField;
    formFields: FormField[];
    fieldColumnPairs: FormFieldListConstraintColumn[];

    private selectedConstraintIsSimpleList: boolean = true;
    private allFieldInfo: AllFieldInfo;
    private selectedMetadataField: FormField;
    private formTemplateId: number;
    private fieldNameErrorMsg: string;
    private form: Form = new Form(); // Used to check for form field name unique values.
    private fieldInsideGridCol: boolean = false;
    private availableSourceFields: FormField[];

    constructor(private formFieldService: FormFieldService,
        private flexibleSelectionFieldService: FlexibleSelectionFieldService,
        private currentSiteService: CurrentSiteService,
        private fieldDefinitionService: FieldDefinitionService,
        private fb: UntypedFormBuilder,
        public dialogRef: MatDialogRef<FlexibleSelectionFieldConfigurationComponent>,
        @Inject(MAT_DIALOG_DATA) public data) {
    }

    ngOnInit(): void {
        this.ngFormGroup = this.fb.group({
            displayName: [this.data.displayName],
            name: [this.data.name],
            toolTip: [this.data.toolTip],
            placeholderText: [this.data.placeholderText],
            instructionsText: [this.data.instructionsText],
            hasFootnote: [this.data.hasFootnote],
            autoCompleteEnabled: [this.data.autoCompleteEnabled],
            minimumSelections: this.data.minimumSelections,
            maximumSelections: this.data.maximumSelections ? this.data.maximumSelections : 1,
            selectedConstraintName: '',
            selectedConstraintColumnName: '',
            ListConstraintColumnId: [this.data.ListConstraintColumnId],
            dependsOnParentFormFieldId: [this.data.dependsOnParentFormFieldId],
            dependsOnParentFormFieldName: [this.data.dependsOnParentFormFieldName],
            formFields: [this.data.formFields],
            fieldColumnPairs: [this.data.fieldColumnPairs],
            required: this.data.required == null ? false: [this.data.required],
            id: [this.data.id],
            defaultValue: this.data.defaultValue == null ? "" : [this.data.defaultValue]
        });
        this.formTemplateId = this.data.formTemplateId;
        this.formField = this.data.formField;
        this.formFields = this.data.formFields;
        this.form.formFields = this.formFields;

        //**Set flag which indicates if the flex field is inside of a grid
        if (this.formField != null && this.formField.gridColClientId > 0)
            this.fieldInsideGridCol = true;

        this.loadingConstraints = true;
        this.formFieldService.getSiteNamedListValueConstraints(this.currentSiteService.Site.id).then(constraints => {
            if (constraints) {
                this.Constraints = constraints;
                let selectedConstraint = this.Constraints.find(c => c.constraintName == this.data.listValuesConstraintName);
                if (selectedConstraint) {
                    this.selectedConstraintIsSimpleList = selectedConstraint.constraintType == FormFieldConstraintTypeEnum.SimpleListValues;
                }
                else if (this.data.listValuesConstraintName) {
                    console.log('Constraint not found with value "' + this.data.listValuesConstraintName + '"');
                }
            }
            else {
                console.log('No List Value Constraints returned for site ID ' + this.currentSiteService.Site.id);
            }
            this.loadingConstraints = false;
        });

        this.fieldColumnPairs = [];
        if (this.data.fieldColumnPairs) {
            for (let index: number = 0; index < this.data.fieldColumnPairs.length; index++) {
                let newCol = new FormFieldListConstraintColumn(this.formField.id, this.data.fieldColumnPairs[index].listConstraintColumnId);
                newCol.constraintName = this.data.fieldColumnPairs[index].constraintName;
                newCol.listConstraintColumnName = this.data.fieldColumnPairs[index].columnName;
                newCol.listConstraintColumnId = this.data.fieldColumnPairs[index].listConstraintColumnId;
                this.fieldColumnPairs.push(newCol);
            }
        }

        this.fieldDefinitionService.getMetadataFields().then(metadataFields => {
            this.allFieldInfo = new AllFieldInfo(this.formFields, metadataFields);

            // Filter out fields which are not allowed to be sources of flex field behavir
            // Also filter out the field currently being configured
            this.availableSourceFields = this.allFieldInfo.AllFieldsSortedByNameAscending.filter(formField => {
                let fieldDefClientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(formField.fieldDefinitionClassName);
                // To add new kind of field to serve as source to a flex field, see https://community-dc.max.gov/x/E4u6mQ
                return fieldDefClientLogic.canParticipateInFlexFieldBehavior &&
                    this.formField.id != formField.id;
                
            });
        });
    }

    public get AvailableSourceFields() {
        return this.availableSourceFields;
    }

    public get NgFormGroup(): UntypedFormGroup {
        return (this.ngFormGroup);
    }

    public get LoadingConstraints(): boolean {
        return this.loadingConstraints;
    }
    public get LoadingColumns(): boolean {
        return this.loadingColumns;
    }

    public get FieldColumnPairs(): FormFieldListConstraintColumn[] {
        return this.fieldColumnPairs;
    }

    public get FieldNameErrorMsg(): string {
        return this.fieldNameErrorMsg;
    }
    public get FieldInsideGridCol(): boolean {
        return this.fieldInsideGridCol;
    }

    public cancelClicked() {
        return;
    }

    public onSelectConstraint(selected) {
        let selectedConstraint = this.Constraints.find(c => c.constraintName == selected.value);
        if (selectedConstraint) {
            this.SelectedConstraintId = selectedConstraint.id;
            this.SelectedColumnId = 0;
            this.loadingColumns = true;
            this.flexibleSelectionFieldService.GetColumns(this.SelectedConstraintId).then(res => {
                this.loadingColumns = false;
                this.Columns = res;
                if (selectedConstraint.constraintType == FormFieldConstraintTypeEnum.SimpleListValues) {
                    this.ngFormGroup.controls.selectedConstraintName.disable();
                    this.ngFormGroup.controls.selectedConstraintColumnName.disable();
                    this.selectedConstraintIsSimpleList = true;

                    let simpleListCol = this.Columns.filter(c => c.formFieldListValuesConstraintId == selectedConstraint.id)[0];
                    if (simpleListCol) {
                        this.fieldColumnPairs = [];
                        let fcp = new FormFieldListConstraintColumn(this.formField.id, simpleListCol.id);
                        fcp.formFieldName = this.formField.name;
                        fcp.constraintId = this.SelectedConstraintId;
                        fcp.constraintName = selectedConstraint.constraintName;
                        fcp.listConstraintColumnId = simpleListCol.id;
                        fcp.listConstraintColumnName = simpleListCol.name;
                        this.fieldColumnPairs.push(fcp);
                    }
                }
                else {
                    this.ngFormGroup.controls.selectedConstraintName.enable();
                    this.selectedConstraintIsSimpleList = false;
                }
            });
        }
        else {
            console.log('Unable to identify constraint selected as ' + selected.value);
        }
    }

    public onSelectColumn(selected) {
        let col = this.Columns.find(c => c.name == selected.value);
        if (col) {
            this.SelectedColumnId = col.id;
            this.addFCpair();
            this.ngFormGroup.controls['selectedConstraintName'].setValue(null);
            this.ngFormGroup.controls['selectedConstraintColumnName'].setValue(null);
        }
        else {
            console.log('Column not found for selected value ' + selected.value);
        }
    }

    public onSelectSourceField(selected): void {
        let selectedSourceField = this.availableSourceFields.find(x => x.name == selected.value);
        if (!selectedSourceField) return;

        let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(selectedSourceField.fieldDefinitionClassName);

        if (fieldDef.isMetadataField && selectedSourceField.id <= 0) {
            if (this.formTemplateId <= 0) {
                alert("In order to set a metadata field, you first need to save the new form.");
                this.ngFormGroup.controls['dependsOnParentFormFieldName'].setValue(null);
                return;
            }
            let metadataFormField: FormField = new FormField();
            metadataFormField.formTemplateId = this.formTemplateId;
            metadataFormField.isMetadataField = true;
            metadataFormField.name = selectedSourceField.fieldDefinitionClassName;
            metadataFormField.displayName = selectedSourceField.displayName;
            metadataFormField.fieldDefinitionClassName = selectedSourceField.fieldDefinitionClassName;
            this.formFieldService.getOrCreateMetaDataField(metadataFormField).then(x => {
                this.selectedMetadataField = x;
                selectedSourceField.id = x.id; // set the Id of the metadata field 
            });
            //metadataFormField.required = selectedSourceField.required;
        }
    }

    public deleteFCpair(FCpair: FormFieldListConstraintColumn) {
        this.fieldColumnPairs = this.fieldColumnPairs.filter(fcp => !(fcp.listConstraintColumnId == FCpair.listConstraintColumnId && fcp.constraintId == FCpair.constraintId));
    }

    public addFCpair() {
        if (this.fieldColumnPairs.find(fcp => fcp.constraintId == this.SelectedConstraintId && fcp.listConstraintColumnId == this.SelectedColumnId)) return; //duplicate
        let fcp = new FormFieldListConstraintColumn(this.formField.id, this.SelectedColumnId);
        fcp.formFieldName = this.formField.name;
        fcp.constraintId = this.SelectedConstraintId;
        fcp.constraintName = this.ngFormGroup.value.selectedConstraintName;
        fcp.listConstraintColumnId = this.SelectedColumnId;
        fcp.listConstraintColumnName = this.ngFormGroup.value.selectedConstraintColumnName;
        this.fieldColumnPairs.push(fcp);
    }

    public onSave() {
        let formVals = this.ngFormGroup.value;
        let sourceField = this.availableSourceFields.find(f => f.name == formVals.dependsOnParentFormFieldName);
        let returnValues = {
            constraintIsSimpleList: this.selectedConstraintIsSimpleList,
            selectConstraintId: this.SelectedConstraintId,
            listConstraintColumnId: this.SelectedColumnId,
            dependsOnParentFormFieldId: sourceField?.id,
            dependsOnParentFormFieldName: sourceField?.displayName,
            dependsOnParentFormFieldDefinitionClassName: sourceField?.fieldDefinitionClassName,
            displayName: formVals.displayName,
            name: formVals.name,
            toolTip: formVals.toolTip,
            placeholderText: formVals.placeholderText,
            instructionsText: formVals.instructionsText,
            hasFootnote: formVals.hasFootnote,
            autoCompleteEnabled: formVals.autoCompleteEnabled,
            fieldColumnPairs: this.fieldColumnPairs,
            selectedMetadataField: this.selectedMetadataField,
            minimumSelections: formVals.minimumSelections,
            maximumSelections: formVals.maximumSelections,
            required: formVals.required,
            id: formVals.id,
            defaultValue: formVals.defaultValue
        }

        this.dialogRef.close(returnValues);
    }

    public handleModelChange(propertyName: string): void {
        let formVals = this.ngFormGroup.value;
        let internalName = formVals.name;
        //debugger;

        if (propertyName === FormFieldPropertyEnum.NAME) {
            this.fieldNameErrorMsg = null;
            if (StringUtil.isEmptyString(internalName)) {
                this.fieldNameErrorMsg = "A field's 'Name' cannot be blank";
            } else if (this.formField.name != internalName && this.form.nameForFormFieldNotUnique(this.formField, internalName)) {
                this.fieldNameErrorMsg = "The field's 'Name' value is not unique";
            } else if (internalName.includes(' ')) {
                this.fieldNameErrorMsg = 'A field internal name cannot contain spaces';
            } else if (internalName != internalName.toLowerCase()) {
                this.fieldNameErrorMsg = 'A field internal name cannot contain capital letters';
            } else if (this.fieldInsideGridCol) {
                //***Perform additional validation if field is inside of a grid field
                if (!isNaN(Number(internalName.charAt(0)))) {
                    this.fieldNameErrorMsg = 'A grid column internal name cannot start with a digit';
                } else if (!(new RegExp('^[a-zA-Z0-9$_]*$').test(internalName))) {
                    this.fieldNameErrorMsg = 'A grid column internal name can contain only letters, numbers, $ and _ symbols';
                }
            }
        }
        //***Leaving these comments for now since the autocomplete logic will be implemenented in the future*/
        //else if (propertyName == FormFieldPropertyEnum.AUTOCOMPLETE) {
        //    if (this.field.autocomplete) {
        //        this.field.autocomplete_StartsWith = true;
        //        this.field.autocomplete_Contains = true;
        //    }
        //}

        //***This logic was used when editing properties via the drawer. Don't think its needed anymore
        // Note: notify my parent that a property has changed.
        //let hshEventData = {
        //    propertyName: propertyName,
        //    formField: this.formField,
        //    cancelEdit: propertyName == FormFieldPropertyEnum.CANCEL_ALL
        //}
        //this.propertyChanged.emit(hshEventData);

        return;
    }

    //public validateRequiredConfigValues(formValues: any): boolean {
    //    let result: boolean = true;

    //    if (this.fieldNameErrorMsg != null)
    //        result = false;
    //    else if (StringUtil.isEmptyString(formValues.name))
    //        result = false;

    //    return result;
    //}
}
